import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EVENT_LOCATION_TYPE_HYBRID,
  EVENT_LOCATION_TYPE_OFFLINE,
  EVENT_LOCATION_TYPE_ONLINE,
  EVENT_TYPE_LOUNGE_WIDE,
  EVENT_TYPE_STUDYGROUP_ONLY,
} from "@/constants";
import LinkifiedText from "@/components/common/LinkifiedText";
import { StepFooter } from "@/features/create-event/components/StepFooter";
import type { EventFormData } from "@/features/study-group-events/type";
import { formatRecurrenceSummary } from "@/lib/utils/event/recurrence";
import {
  formatEventLocationDisplay,
  formatReminderText,
} from "@/lib/utils/eventUtils";
import { getLanguageLabel } from "@/lib/utils/textUtils";
import { formatEventReview } from "@/lib/utils/v2/event-date";

const LabelWithData = ({
  label,
  data,
  className = "",
}: {
  label: string;
  data: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={`rounded-lg border border-gray-100 bg-white/80 px-3 py-2.5 ${className}`}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-1.5 text-sm leading-5 text-rw-black-gray">{data}</div>
    </div>
  );
};

const PlainValue: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="break-words font-medium text-rw-black-gray">{children}</div>
);

const ValueList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="space-y-1">
    {items.map((item, index) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: derived from display-only list
      <li key={index} className="text-sm font-medium text-rw-black-gray">
        {item}
      </li>
    ))}
  </ul>
);

const ValueWithOptionalScroll: React.FC<{
  children: React.ReactNode;
  shouldScroll?: boolean;
  maxHeightClassName?: string;
}> = ({ children, shouldScroll = false, maxHeightClassName = "max-h-24" }) => {
  if (!shouldScroll) {
    return <PlainValue>{children}</PlainValue>;
  }

  return (
    <div
      className={`${maxHeightClassName} overflow-y-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2`}
    >
      <div className="break-words font-medium text-rw-black-gray">
        {children}
      </div>
    </div>
  );
};

const UnsetValue: React.FC<{ label: string }> = ({ label }) => (
  <span className="text-sm font-normal text-gray-500">{label}</span>
);

const ScrollableValue: React.FC<{
  children: React.ReactNode;
  maxHeightClassName?: string;
}> = ({ children, maxHeightClassName = "max-h-32" }) => (
  <div
    className={`${maxHeightClassName} overflow-y-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2`}
  >
    <div className="break-words font-medium text-rw-black-gray">{children}</div>
  </div>
);

const ValueBlock: React.FC<{
  value?: string | null;
  notSetLabel: string;
  scrollThreshold?: number;
  maxHeightClassName?: string;
}> = ({
  value,
  notSetLabel,
  scrollThreshold = 80,
  maxHeightClassName = "max-h-24",
}) => {
  if (!value) {
    return <UnsetValue label={notSetLabel} />;
  }

  return (
    <ValueWithOptionalScroll
      shouldScroll={value.length > scrollThreshold}
      maxHeightClassName={maxHeightClassName}
    >
      {value}
    </ValueWithOptionalScroll>
  );
};

const LocationRow: React.FC<{
  label: string;
  value?: string | null;
  notSetLabel: string;
}> = ({ label, value, notSetLabel }) => (
  <div className="space-y-0.5">
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
      {label}
    </div>
    <div className="text-rw-black-gray">
      {value ? (
        <ScrollableValue maxHeightClassName="max-h-20">
          <LinkifiedText text={value} className="block" />
        </ScrollableValue>
      ) : (
        <UnsetValue label={notSetLabel} />
      )}
    </div>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <h2 className="text-lg font-semibold text-rw-black sm:text-xl">
      {children}
    </h2>
  );
};

const ReviewGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
);

const FullWidthReviewGrid: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="space-y-3">{children}</div>;

const DescriptionValue: React.FC<{ value: string }> = ({ value }) => {
  const shouldScroll = value.length > 160 || value.includes("\n");
  return shouldScroll ? (
    <ScrollableValue maxHeightClassName="max-h-32">
      <LinkifiedText text={value} className="block" />
    </ScrollableValue>
  ) : (
    <PlainValue>
      <LinkifiedText text={value} className="block" />
    </PlainValue>
  );
};

const ReminderValue: React.FC<{ items: string[] }> = ({ items }) => {
  if (items.length <= 2) {
    return <ValueList items={items} />;
  }

  return (
    <ScrollableValue maxHeightClassName="max-h-28">
      <ValueList items={items} />
    </ScrollableValue>
  );
};

const LinkifiedValue: React.FC<{
  value: string;
  scrollThreshold?: number;
}> = ({ value, scrollThreshold = 80 }) => {
  const shouldScroll = value.length > scrollThreshold;
  if (!shouldScroll) {
    return <LinkifiedText text={value} className="block font-medium" />;
  }

  return (
    <ScrollableValue maxHeightClassName="max-h-20">
      <LinkifiedText text={value} className="block" />
    </ScrollableValue>
  );
};

const LocationValue: React.FC<{
  formData: EventFormData;
  locationInfo: ReturnType<typeof formatEventLocationDisplay>;
  notSetLabel: string;
  t: ReturnType<typeof useTranslation>["t"];
}> = ({ formData, locationInfo, notSetLabel, t }) => {
  const hasLocationValue =
    Boolean(formData.location?.trim()) ||
    Boolean(formData.online_location?.trim());
  const hasLocationType = formData.location_type != null;

  if (formData.location_type === EVENT_LOCATION_TYPE_ONLINE) {
    return (
      <div className="space-y-2">
        <PlainValue>{locationInfo.type}</PlainValue>
        {hasLocationValue && (
          <LocationRow
            label={t("common.eventUrl")}
            value={formData.online_location}
            notSetLabel={notSetLabel}
          />
        )}
      </div>
    );
  }

  if (formData.location_type === EVENT_LOCATION_TYPE_HYBRID) {
    return (
      <div className="space-y-2">
        <PlainValue>{locationInfo.type}</PlainValue>
        {hasLocationValue && (
          <>
            <LocationRow
              label={t("eventCreate.physicalLocation")}
              value={formData.location}
              notSetLabel={notSetLabel}
            />
            <LocationRow
              label={t("common.eventUrl")}
              value={formData.online_location}
              notSetLabel={notSetLabel}
            />
          </>
        )}
      </div>
    );
  }

  if (formData.location_type === EVENT_LOCATION_TYPE_OFFLINE) {
    return (
      <div className="space-y-2">
        <PlainValue>{locationInfo.type}</PlainValue>
        {hasLocationValue && (
          <LocationRow
            label={t("eventCreate.physicalLocation")}
            value={formData.location}
            notSetLabel={notSetLabel}
          />
        )}
      </div>
    );
  }

  if (!hasLocationValue && !hasLocationType) {
    return <UnsetValue label={notSetLabel} />;
  }

  return <LinkifiedValue value={locationInfo.details} />;
};

const hasMeaningfulImage = (imageUrl: string | null) => Boolean(imageUrl);

const shouldShowDescription = (description?: string) =>
  Boolean(description?.trim());

const shouldShowRecurrence = (recurrence: EventFormData["recurrence"]) =>
  Boolean(recurrence);

const shouldShowLocation = (formData: EventFormData) =>
  formData.location_type != null ||
  Boolean(formData.location?.trim()) ||
  Boolean(formData.online_location?.trim());

const shouldShowReminders = (reminders: EventFormData["reminders"]) =>
  reminders.length > 0;

interface StepReviewProps {
  formData: EventFormData;
  handleNext?: () => void;
  handleSaveDraft?: () => void;
  setStep?: (value: React.SetStateAction<number>) => void;
  isSubmitting?: boolean;
}

export const StepReview: React.FC<StepReviewProps> = ({
  formData,
  handleNext,
  handleSaveDraft,
  setStep,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const [featuredImagePreviewUrl, setFeaturedImagePreviewUrl] = useState<
    string | null
  >(null);

  useEffect(() => {
    const featuredImageFile = formData.files[0];
    if (!featuredImageFile) {
      setFeaturedImagePreviewUrl(null);
      return;
    }

    const previewUrl = URL.createObjectURL(featuredImageFile);
    setFeaturedImagePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [formData.files]);

  const locationInfo = formatEventLocationDisplay(
    {
      locationType: formData.location_type,
      location: formData.location,
      onlineLocation: formData.online_location,
      legacyLocation: formData.location,
    },
    t,
  );
  const notSetLabel = t("common.notSet");
  const eventDatetime = formatEventReview(formData);
  const recurrenceSummary =
    formatRecurrenceSummary(formData.recurrence, formData.startDate, t) ||
    t("common.recurrenceSummaryOneTime", "One-time");
  const reminderItems = formData.reminders.map(
    (reminder) => `${formatReminderText(reminder, t)} ${t("common.before")}`,
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="space-y-4">
          <SectionTitle>{t("eventCreate.reviewEventDetails")}</SectionTitle>

          <ReviewGrid>
            <LabelWithData
              label={t("eventCreate.eventName")}
              data={
                formData.title ? (
                  <ValueBlock
                    value={formData.title}
                    notSetLabel={notSetLabel}
                  />
                ) : (
                  <UnsetValue label={notSetLabel} />
                )
              }
              className="sm:col-span-2"
            />
            <LabelWithData
              label={t("common.language")}
              data={
                getLanguageLabel(formData.language) ? (
                  <PlainValue>{getLanguageLabel(formData.language)}</PlainValue>
                ) : (
                  <UnsetValue label={notSetLabel} />
                )
              }
            />
            <LabelWithData
              label={t("common.timeZone")}
              data={
                formData.timeZone ? (
                  <PlainValue>{formData.timeZone}</PlainValue>
                ) : (
                  <UnsetValue label={notSetLabel} />
                )
              }
            />
            <LabelWithData
              label={t("eventCreate.eventDatetime")}
              data={
                eventDatetime ? (
                  <PlainValue>{eventDatetime}</PlainValue>
                ) : (
                  <UnsetValue label={notSetLabel} />
                )
              }
              className="sm:col-span-2"
            />
            {shouldShowRecurrence(formData.recurrence) && (
              <LabelWithData
                label={t("common.enableRecurrence")}
                data={
                  <ValueBlock
                    value={recurrenceSummary}
                    notSetLabel={notSetLabel}
                  />
                }
                className="sm:col-span-2"
              />
            )}
          </ReviewGrid>

          <FullWidthReviewGrid>
            <LabelWithData
              label={t("eventManage.eventVisibility")}
              data={
                formData.is_public === EVENT_TYPE_LOUNGE_WIDE ? (
                  <PlainValue>{t("eventManage.loungeWide")}</PlainValue>
                ) : formData.is_public === EVENT_TYPE_STUDYGROUP_ONLY ? (
                  <PlainValue>{t("eventManage.studyGroupOnly")}</PlainValue>
                ) : (
                  <UnsetValue label={notSetLabel} />
                )
              }
            />
            {shouldShowLocation(formData) && (
              <LabelWithData
                label={t("common.location")}
                data={
                  <LocationValue
                    formData={formData}
                    locationInfo={locationInfo}
                    notSetLabel={notSetLabel}
                    t={t}
                  />
                }
              />
            )}
            {shouldShowDescription(formData.description) && (
              <LabelWithData
                label={t("common.description")}
                data={<DescriptionValue value={formData.description} />}
              />
            )}
            {hasMeaningfulImage(featuredImagePreviewUrl) && (
              <LabelWithData
                label={t("eventCreate.featuredImage")}
                data={
                  <img
                    src={featuredImagePreviewUrl ?? undefined}
                    alt="Featured preview"
                    className="h-16 w-24 rounded border object-cover"
                  />
                }
              />
            )}
            {shouldShowReminders(formData.reminders) && (
              <LabelWithData
                label={t("eventCreate.reminderTitle")}
                data={<ReminderValue items={reminderItems} />}
              />
            )}
          </FullWidthReviewGrid>
        </div>
      </div>

      <div className="shrink-0 border-t border-gray-100 px-4 py-3 sm:px-6">
        <StepFooter
          isSubmitting={isSubmitting}
          onBack={() => setStep?.((prev) => Math.max(1, prev - 1))}
          onSaveDraft={handleSaveDraft}
          onNext={handleNext}
          nextLabel={t("eventManage.publishEvent")}
        />
      </div>
    </div>
  );
};
